import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { formatAssetStatus } from '../../../utils/formatters';


export function FilterToolbar({ 
  filters: propFilters, 
  onFilterChange, 
  onBulkAction, 
  selectedCount = 0, 
  totalCount = 0,
  onExport,
  departments = [], 
  suppliers = [],   
  locations = [],
  hideDepartmentFilter = false
}) {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(propFilters || {
    searchQuery: '',
    category: '',
    status: [],
    department: '',
    supplier: '',
    location: '',
    dateRange: { start: '', end: '' }
  });

  React.useEffect(() => {
    if (propFilters) {
      setLocalFilters(propFilters);
    }
  }, [propFilters]);


  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'Laptop', label: 'Laptop' },
    { value: 'Desktop', label: 'Desktop' },
    { value: 'Monitor', label: 'Monitor' },
    { value: 'Printer', label: 'Printer' },
    { value: 'Server', label: 'Server' },
    { value: 'Network', label: 'Network' },
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Tablet', label: 'Tablet' },
    { value: 'Software', label: 'Software' },
    { value: 'Other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'Available', label: formatAssetStatus('Available') },
    { value: 'In Use', label: formatAssetStatus('In Use') },
    { value: 'In Repair', label: formatAssetStatus('In Repair') },
    { value: 'Broken', label: formatAssetStatus('Broken') },
    { value: 'Written Off', label: formatAssetStatus('Written Off') },
    { value: 'Lost/Stolen', label: formatAssetStatus('Lost/Stolen') }
  ];

  const locationOptions = [
    { value: '', label: t('asset_list.all_locations') },
    ...locations
  ];

  const bulkActionOptions = [
    { value: 'update_status', label: t('asset_list.update_status') },
    { value: 'transfer_location', label: t('asset_list.transfer_location') },
    { value: 'assign_user', label: t('asset_list.assign_to_user') },
    { value: 'generate_qr', label: t('asset_list.generate_qr_codes') },
    { value: 'export_selected', label: t('asset_list.export_selected') }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters); 
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = { ...localFilters?.dateRange, [field]: value };
    const newFilters = { ...localFilters, dateRange: newDateRange };
    setLocalFilters(newFilters);
    onFilterChange(newFilters); 
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      searchQuery: '',
      category: '',
      status: [],
      department: hideDepartmentFilter ? localFilters.department : '',
      supplier: '',
      location: '',
      dateRange: { start: '', end: '' }
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters); 
  };

  const hasActiveFilters = localFilters?.searchQuery || localFilters?.category || localFilters?.status?.length > 0 || 
                           (!hideDepartmentFilter && localFilters?.department) || localFilters?.supplier || localFilters?.location || 
                           localFilters?.dateRange?.start || localFilters?.dateRange?.end;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Search Input */}
      <div className="relative mb-4">
        <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('asset_list.search_placeholder')}
          className="w-full pl-10 pr-4 py-2 bg-card border-input text-black"
          value={localFilters?.searchQuery || ''}
          onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
        />
      </div>

      {/* Filter Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('labels.category')}</span>
          <Select
            options={categoryOptions}
            value={localFilters?.category}
            onChange={(value) => handleFilterChange('category', value)}
            placeholder={t('asset_list.select_category')}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('labels.status')}</span>
          <Select
            options={statusOptions}
            value={localFilters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            multiple
            searchable
            placeholder={t('asset_list.select_status')}
            className="w-full"
          />
        </div>

        {!hideDepartmentFilter && (
          <div className="space-y-2">
            <span className="block text-sm font-medium text-foreground">{t('labels.department')}</span>
            <Select
              options={[{ value: '', label: t('asset_list.all_departments') }, ...departments]}
              value={localFilters?.department}
              onChange={(value) => handleFilterChange('department', value)}
              placeholder={t('asset_list.select_department')}
              className="w-full"
            />
          </div>
        )}

        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('labels.supplier')}</span>
          <Select
            options={[{ value: '', label: t('asset_list.all_suppliers') }, ...suppliers]}
            value={localFilters?.supplier}
            onChange={(value) => handleFilterChange('supplier', value)}
            placeholder={t('asset_list.select_supplier')}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('labels.location')}</span>
          <Select
            options={locationOptions}
            value={localFilters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            placeholder={t('asset_list.select_location')}
            className="w-full"
          />
        </div>

        <div className="space-y-2 sm:col-span-2 lg:col-span-2">
          <span className="block text-sm font-medium text-foreground">{t('asset_list.date_registered')}</span>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="w-full sm:max-w-48">
              <Input
                id="start-date"
                type="date"
                className="w-full bg-card border-input text-foreground text-sm"
                value={localFilters?.dateRange?.start || ''}
                onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
              />
            </div>
            <span className="text-muted-foreground text-xs sm:text-sm font-medium self-center sm:px-1">{t('common.to') || 'to'}</span>
            <div className="w-full sm:max-w-48">
              <Input
                id="end-date"
                type="date"
                className="w-full bg-card border-input text-foreground text-sm"
                value={localFilters?.dateRange?.end || ''}
                onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-muted-foreground">
            {t('asset_list.showing_assets', { count: totalCount })}
            {hasActiveFilters && (
              <span className="ml-2 text-accent">
                {t('asset_list.filtered')}
              </span>
            )}
          </div>
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={clearAllFilters}
            >
                {t('asset_list.clear_filters')}
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {selectedCount > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {t('asset_list.selected_count', { count: selectedCount })}
              </span>
              
              <Select
                options={bulkActionOptions}
                value="" 
                onChange={(value) => onBulkAction(value)}
                placeholder={t('asset_list.bulk_actions')}
                className="w-40"
              />
            </>
          )}

          <Button
            variant="outline"
            iconName="Download"
            onClick={onExport}
          >
            {t('asset_list.export_csv')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FilterToolbar;
